import React, { useState } from "react";
import { Logotype } from "../../Components/Logotype/Logotype";
import { SvgIcon } from "../../Components/SvgIcon/SvgIcon";
import { NavigationDropdown } from "./NavigationDropdown";
import { MobileNavigation } from "./MobileNavigation";
import classnames from "classnames";
import { dontFireOnTouchEventOnTargetHandler } from "../../Components/Helpers/clickAwayListenerHelpers";
import ClickAwayListener from "react-click-away-listener";
import { NavigationProps } from "../TopBar/NavigationProps.csharp";
import { NavigationItemViewModel } from "../TopBar/NavigationItemViewModel.csharp";
import FocusTrap from "focus-trap-react";

export interface NavigationMobileMenuProps {
  showMobileMenu: boolean;
  setShowMobileMenu: (value: boolean) => void;
}

export const Navigation: React.FC<
  NavigationProps & NavigationMobileMenuProps
> = ({ navigationItems, logoUrl, showMobileMenu, setShowMobileMenu }) => {
  const [activeItem, setActiveItem] = useState<NavigationItemViewModel>();
  const [mobileMenuIsActive, setMobileMenuActive] = useState(false);
  const [megaMenuIsOpen, setMegaMenuActive] = useState(false);
  const [focusTrapActive, setFocusTrapActive] = useState(false);

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    setMobileMenuActive(!mobileMenuIsActive);
  };

  const toggleDesktopMegamenu = (
    e: any,
    item: NavigationItemViewModel | undefined = undefined
  ) => {
    if (activeItem === undefined || activeItem !== item) {
      setActiveItem(item);
    } else {
      hideDropdown();
    }
    setMegaMenuActive(!megaMenuIsOpen);
    setMobileMenuActive(!mobileMenuIsActive);

    if (e.clientX === 0 && !focusTrapActive) {
      setFocusTrapActive(true);
    } else {
      setFocusTrapActive(false);
    }
  };

  const hideDropdown = () => setActiveItem(undefined);
  const onClickAwayDesktop = (e: Event) =>
    dontFireOnTouchEventOnTargetHandler(
      "button-navigation",
      () => toggleDesktopMegamenu(event, undefined),
      e
    );
  const onClickAwayMobile = (e: Event) =>
    dontFireOnTouchEventOnTargetHandler(
      "button-navigation",
      () => toggleMobileMenu(),
      e
    );

  return (
    <nav>
      <h2 className="visually-hidden">Hovedmeny</h2>
      <div className="navbar navbar-expand-xl px-0 py-1 py-xl-0 pt-xl-2">
        <Logotype logoUrl={logoUrl} />
        <button
          className={classnames("button-navigation navbar-toggler", {
            active: mobileMenuIsActive,
          })}
          aria-label="Vis/skjul Meny"
          onClick={toggleMobileMenu}
        >
          <span className="button-navigation navbar-toggler-icon">
            <SvgIcon iconName="burger-menu" />
          </span>
        </button>

        <ul className="navbar-nav d-none d-xl-flex">
          {(navigationItems ?? []).map((item, index) => (
            <li
              className={classnames("nav-item", {
                active: item == activeItem,
              })}
              key={index}
            >
              {item.linkSections.length === 0 && (
                <a className="nav-link py-1 mb-2" href={item.heading.url}>
                  {item.heading.title}
                </a>
              )}
              {item.linkSections.length > 0 && (
                <button
                  aria-haspopup="true"
                  aria-expanded={focusTrapActive}
                  aria-controls="desktopMenu"
                  className={`button-navigation nav-link nav-link-expand-children py-1 mb-2`}
                  onClick={(event) => {
                    toggleDesktopMegamenu(event, item);
                  }}
                >
                  {item.heading.title}
                  <i className="icon icon-sm icon-v" />
                  <span className="visually-hidden">
                    Vis flere underniv&aring;er {item.heading.title}
                  </span>
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {activeItem && (
        <FocusTrap
          active={focusTrapActive}
          focusTrapOptions={{
            onDeactivate: () => toggleDesktopMegamenu(event, undefined),
          }}
        >
          <div
            id="desktopMenu"
            className="megamenu megamenu-desktop d-none d-xl-block px-0"
          >
            <div className="megamenu-cover" />
            <ClickAwayListener onClickAway={onClickAwayDesktop}>
              <div className="megamenu-menu">
                <NavigationDropdown
                  linkSections={activeItem.linkSections}
                  additionalLink={activeItem.additionalLink}
                />
                <button
                  className="btn-close btn-close-white"
                  onClick={(event) => toggleDesktopMegamenu(event)}
                >
                  Lukk <span className="visually-hidden">meny (x)</span>
                </button>
              </div>
            </ClickAwayListener>
          </div>
        </FocusTrap>
      )}
      {showMobileMenu && (
        <div className="megamenu megamenu-mobile d-xl-none">
          <div className="megamenu-cover" />
          <ClickAwayListener onClickAway={onClickAwayMobile}>
            <div className="megamenu-menu slide-in-from-right">
              <MobileNavigation navigationItems={navigationItems} />
            </div>
          </ClickAwayListener>
        </div>
      )}
    </nav>
  );
};
